import { faPlay, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef } from 'react';

const getThumbnail = (cap) =>
  cap.thumbnail || cap.url.replace('turkish4love.com/videos/', 'turkish4love.com/thumbnails/').replace('.mp4', '.jpeg');

const ProgressBar = ({ time, duration }) => {
  const pct = Math.min((time / duration) * 100, 100);
  return (
    <div className='absolute bottom-0 left-0 right-0 h-1 bg-black/30'>
      <div className='h-full bg-red-1' style={{ width: `${pct}%` }} />
    </div>
  );
};

const EpisodeItem = ({ cap, active, onSelect }) => {
  const watched = !!cap.completedAt;
  const hasProgress = !watched && cap.time > 5 && cap.duration > 0;

  return (
    <button
      onClick={() => onSelect(cap.numero)}
      className={`w-full flex items-center gap-3 px-3 py-3 border-b border-gray-100 hover:bg-gray-50 text-left transition-colors ${
        active ? 'bg-red-50 border-l-4 border-l-red-1' : 'border-l-4 border-l-transparent'
      }`}
    >
      <div className='relative w-16 aspect-video rounded overflow-hidden flex-shrink-0 bg-gray-200'>
        <img
          src={getThumbnail(cap)}
          alt={`Episodio ${cap.numero}`}
          className='w-full h-full object-cover'
          loading='lazy'
        />
        <div className='absolute inset-0 flex items-center justify-center bg-black/20'>
          <FontAwesomeIcon icon={faPlay} className='text-white text-xs drop-shadow' />
        </div>
        {watched && (
          <div className='absolute inset-0 bg-black/40 flex items-center justify-center'>
            <FontAwesomeIcon icon={faCheck} className='text-white text-sm drop-shadow' />
          </div>
        )}
        {hasProgress && <ProgressBar time={cap.time} duration={cap.duration} />}
      </div>

      <div className='min-w-0 flex-1'>
        <p className={`text-xs font-semibold font-montserrat uppercase tracking-wide ${active ? 'text-red-1' : 'text-gray-400'}`}>
          EP {cap.numero}
        </p>
        <p className='text-sm text-gray-800 font-montserrat truncate'>Episodio {cap.numero}</p>
        {watched && (
          <span className='inline-block mt-0.5 bg-green-100 text-green-700 text-[10px] font-semibold font-montserrat px-1.5 py-0.5 rounded-full leading-none'>
            Visto
          </span>
        )}
      </div>
    </button>
  );
};

const MobileEpisodeCard = ({ cap, active, onSelect }) => {
  const watched = !!cap.completedAt;
  const hasProgress = !watched && cap.time > 5 && cap.duration > 0;

  return (
    <button
      onClick={() => onSelect(cap.numero)}
      className={`flex-shrink-0 w-40 flex flex-col rounded-lg overflow-hidden border shadow text-left transition-colors ${
        active ? 'border-red-1' : 'border-gray-100'
      } bg-white`}
    >
      <div className='relative w-full aspect-video bg-gray-200'>
        <img
          src={getThumbnail(cap)}
          alt={`Episodio ${cap.numero}`}
          className='w-full h-full object-cover'
          loading='lazy'
        />
        <div className='absolute inset-0 flex items-center justify-center bg-black/20'>
          <FontAwesomeIcon icon={faPlay} className='text-white text-xs drop-shadow' />
        </div>
        {watched && (
          <div className='absolute inset-0 bg-black/40 flex items-center justify-center'>
            <FontAwesomeIcon icon={faCheck} className='text-white text-sm drop-shadow' />
          </div>
        )}
        {hasProgress && <ProgressBar time={cap.time} duration={cap.duration} />}
      </div>

      <div className='px-2 py-1.5'>
        <p className={`text-[10px] font-semibold font-montserrat uppercase tracking-wide ${active ? 'text-red-1' : 'text-gray-400'}`}>
          EP {cap.numero}
        </p>
        <p className='text-xs text-gray-800 font-montserrat truncate'>Episodio {cap.numero}</p>
        {watched && (
          <span className='inline-block mt-0.5 bg-green-100 text-green-700 text-[10px] font-semibold font-montserrat px-1.5 py-0.5 rounded-full leading-none'>
            Visto
          </span>
        )}
      </div>
    </button>
  );
};

export const MobileEpisodeScroll = ({ capitulos, activeEpisodeNum, onSelectEpisode }) => {
  const activeRef = useRef(null);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
    }
  }, [activeEpisodeNum]);

  if (!capitulos) {
    return (
      <div className='flex gap-2 overflow-x-auto p-2'>
        {[...Array(6)].map((_, i) => (
          <div key={i} className='flex-shrink-0 w-40 rounded-lg overflow-hidden bg-gray-200 animate-pulse aspect-video' />
        ))}
      </div>
    );
  }

  return (
    <div className='flex gap-2 overflow-x-auto p-2'>
      {capitulos.map((cap) => (
        <div key={cap.numero} ref={cap.numero === activeEpisodeNum ? activeRef : null}>
          <MobileEpisodeCard
            cap={cap}
            active={cap.numero === activeEpisodeNum}
            onSelect={onSelectEpisode}
          />
        </div>
      ))}
    </div>
  );
};

const EpisodeList = ({ capitulos, activeEpisodeNum, onSelectEpisode, maxHeight }) => {
  const activeRef = useRef(null);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [activeEpisodeNum]);

  return (
    <div
      className='hidden md:flex flex-col w-72 flex-shrink-0 bg-white rounded-lg shadow overflow-hidden'
      style={maxHeight ? { maxHeight: `${maxHeight}px` } : undefined}
    >
      <div className='flex items-center justify-between px-4 py-3 border-b border-gray-100'>
        <span className='font-montserrat font-semibold text-xs text-gray-500 uppercase tracking-wider'>Episodios</span>
        {capitulos?.length > 0 && (
          <span className='text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full font-montserrat'>
            {capitulos.length} eps
          </span>
        )}
      </div>
      <div className='overflow-y-auto flex-1'>
        {capitulos?.map((cap) => (
          <div key={cap.numero} ref={cap.numero === activeEpisodeNum ? activeRef : null}>
            <EpisodeItem
              cap={cap}
              active={cap.numero === activeEpisodeNum}
              onSelect={onSelectEpisode}
            />
          </div>
        ))}
        {!capitulos && (
          <div className='flex flex-col gap-2 p-3'>
            {[...Array(8)].map((_, i) => (
              <div key={i} className='flex items-center gap-3'>
                <div className='w-16 aspect-video bg-gray-200 animate-pulse rounded' />
                <div className='flex-1'>
                  <div className='h-3 w-10 bg-gray-200 animate-pulse rounded mb-1' />
                  <div className='h-4 w-24 bg-gray-100 animate-pulse rounded' />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EpisodeList;
